import { useEffect } from 'react';

const getScrollbarWidth = () => window.innerWidth - document.documentElement.clientWidth;

let lockedScrollY = 0;

const isInsideAllowedScroller = (event) => {
  // Use composedPath() to traverse through shadow DOM boundaries (e.g. WaDialog).
  // event.target.closest() does not cross shadow DOM boundaries on iOS Safari,
  // which prevents horizontal scrolling on elements like the search tabs.
  const path = event.composedPath?.() || [];
  return path.some((node) => node?.hasAttribute?.('data-scroll-lock-ignore'));
};

const preventTouchMove = (event) => {
  if (isInsideAllowedScroller(event)) {
    return; // allow scrolling inside modal results.
  }

  event.preventDefault(); // block background scroll.
};

const lockBodyScroll = () => {
  lockedScrollY = window.scrollY || 0;
  Object.assign(document.body.style, {
    position: 'fixed',
    top: `-${lockedScrollY}px`,
    width: '100%',
    overflow: 'hidden',
    paddingRight: `${getScrollbarWidth()}px`,
  });
  window.addEventListener('touchmove', preventTouchMove, { passive: false });
};

const unlockBodyScroll = () => {
  Object.assign(document.body.style, {
    position: '',
    top: '',
    width: '',
    overflow: '',
    paddingRight: '',
  });
  window.removeEventListener('touchmove', preventTouchMove);
  window.scrollTo(0, lockedScrollY);
};

export default ({
  dialogElementRef,
  searchInputRef,
  isOpen,
  onClose,
  onAfterOpen,
  onArrowDown,
  onArrowUp,
  onArrowLeft,
  onArrowRight,
  onEnter,
}) => {
  useEffect(() => {
    const dialogElement = dialogElementRef.current;
    if (!dialogElement) return undefined;

    dialogElement.open = isOpen;

    const handleHide = () => onClose();

    const handleAfterShow = () => {
      if (!searchInputRef.current) return;

      window.requestAnimationFrame(() => {
        searchInputRef.current.focus();
        searchInputRef.current.select();
      });

      if (onAfterOpen) onAfterOpen();
    };

    dialogElement.addEventListener('wa-hide', handleHide);
    dialogElement.addEventListener('wa-after-show', handleAfterShow);

    return () => {
      dialogElement.removeEventListener('wa-hide', handleHide);
      dialogElement.removeEventListener('wa-after-show', handleAfterShow);
    };
  }, [dialogElementRef, searchInputRef, isOpen, onClose, onAfterOpen]);

  useEffect(() => {
    if (!isOpen) {
      unlockBodyScroll();
      return undefined;
    }

    lockBodyScroll();
    return () => unlockBodyScroll();
  }, [isOpen]);

  // useDialogBehavior.js
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      // Prevent key repeat spamming (tabs / navigation)
      if (event.repeat && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
        event.preventDefault();
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        onArrowDown?.();
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        onArrowUp?.();
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        onArrowLeft?.();
        return;
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        onArrowRight?.();
        return;
      }

      if (event.key === 'Enter') {
        onEnter?.(event);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, onArrowDown, onArrowUp, onArrowLeft, onArrowRight, onEnter]);
};
